import { slice, take } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
// ADDED [10/07/2026] - protection-review needs its own weekday/hours rules but
// has no `referral` value in AppContext, so the route path is used instead.
import { useLocation } from "react-router-dom";
import { ButtonSmall } from "../Buttons";
import RadioGroup from "./RadioGroup";
import SingleCheckbox from "./SingleCheckbox";

const AppointmentPicker = ({ data, setData, setAppointment }) => {
  const [formDataChange, setFormDataChange] = useState({});
  const [dateOptions, setDateOptions] = useState([]);
  const [dateOptionsPage, setDateOptionsPage] = useState(0);

  // ADDED [10/07/2026] - flags the protection-review page so buildDates/buildTimeSlots
  // below can apply weekday-only, 9am-6pm rules just for this route.
  const location = useLocation();
  const isProtectionReview = location.pathname.includes("protection-review");

  // Utility helpers defined first so they can be used in dates/timeSlots arrays below.
  const isToday = useCallback((value) => {
    return moment(value).date() === moment().date();
  }, []);

  const isTomorrow = useCallback((value) => {
    return moment(value).date() === moment().add(1, "days").date();
  }, []);

  const isWeekend = useCallback((value) => {
    return moment(value).day() === 0 || moment(value).day() === 6;
  }, []);

  const formatDate = useCallback(
    (date) => {
      if (isToday(date)) {
        return (
          <p className="text-center">
            Today
            <br />
            <span className="text-sm font-normal">
              {moment(date).format("ddd")}
            </span>{" "}
            <span className="font-black">{moment(date).format("DD")}</span>{" "}
            <span className="font-normal">{moment(date).format("MMM")}</span>
          </p>
        );
      }
      if (isTomorrow(date)) {
        return (
          <p className="text-center">
            Tomorrow
            <br />
            <span className="text-sm font-normal">
              {moment(date).format("ddd")}
            </span>{" "}
            <span className="font-black">{moment(date).format("DD")}</span>{" "}
            <span className="font-normal">{moment(date).format("MMM")}</span>
          </p>
        );
      }
      return (
        <p className="text-center">
          <span className="text-lg font-normal">
            {moment(date).format("ddd")}
          </span>
          <br />
          <span className="text-4xl font-black">
            {moment(date).format("DD")}
          </span>
          <br />
          <span className="text-2xl font-normal">
            {moment(date).format("MMM")}
          </span>
        </p>
      );
    },
    [isToday, isTomorrow],
  );

  const buildDates = useCallback(() => {
    const d = (n) => moment().add(n, "days");

    // ADDED [10/07/2026] - "Today" shouldn't be selectable once the current
    // time is past the last bookable slot for today (5:30pm for
    // protection-review, 7pm otherwise) — start the list from tomorrow instead.
    const lastSlotTime = isProtectionReview
      ? moment().hour(17).minute(30)
      : moment().hour(19).minute(0);
    const startOffset = moment().isAfter(lastSlotTime) ? 1 : 0;

    // AMENDED [10/07/2026] - protection-review only offers weekdays, so we scan
    // forward day-by-day and skip Sat/Sun until 20 usable dates are collected.
    // Previously:
    // return Array.from({ length: 20 }, (_, i) => ({
    //   _id: i,
    //   value: d(i).format("YYYY-MM-DD"),
    //   option: formatDate(d(i).format()),
    // }));
    if (isProtectionReview) {
      const weekdayDates = [];
      let i = startOffset;
      while (weekdayDates.length < 20) {
        const day = d(i).day();
        if (day !== 0 && day !== 6) {
          weekdayDates.push({
            _id: weekdayDates.length,
            value: d(i).format("YYYY-MM-DD"),
            option: formatDate(d(i).format()),
          });
        }
        i += 1;
      }
      return weekdayDates;
    }

    // AMENDED [10/07/2026] - offset by startOffset so "today" drops out of the
    // list once its last slot has passed.
    // Previously:
    // return Array.from({ length: 20 }, (_, i) => ({
    //   _id: i,
    //   value: d(i).format("YYYY-MM-DD"),
    //   option: formatDate(d(i).format()),
    // }));
    return Array.from({ length: 20 }, (_, i) => ({
      _id: i,
      value: d(i + startOffset).format("YYYY-MM-DD"),
      option: formatDate(d(i + startOffset).format()),
    }));
  }, [formatDate, isProtectionReview]);

  const checkTimeSlot = useCallback(
    (time) => {
      return isToday(data.appointmentDay)
        ? moment.duration(moment().diff(moment(time))).asHours() > -1
        : false;
    },
    [isToday, data.appointmentDay],
  );

  // AMENDED [10/07/2026] - wrapped the original array in `allSlots` so it can be
  // filtered for protection-review (9am-6pm only) without touching the 21
  // hardcoded slot entries below. See the filter added after the array.
  const buildTimeSlots = useCallback(() => {
    const allSlots = [
      {
        _id: 0,
        group: 0,
        value: 0,
        option: "9:00am",
        disabled: checkTimeSlot(moment().hour(9).minute(0)),
      },
      {
        _id: 1,
        group: 0,
        value: 1,
        option: "9:30am",
        disabled: checkTimeSlot(moment().hour(9).minute(30)),
      },
      {
        _id: 2,
        group: 0,
        value: 2,
        option: "10:00am",
        disabled: checkTimeSlot(moment().hour(10).minute(0)),
      },
      {
        _id: 3,
        group: 0,
        value: 3,
        option: "10:30am",
        disabled: checkTimeSlot(moment().hour(10).minute(30)),
      },
      {
        _id: 4,
        group: 0,
        value: 4,
        option: "11:00am",
        disabled: checkTimeSlot(moment().hour(11).minute(0)),
      },
      {
        _id: 5,
        group: 0,
        value: 5,
        option: "11:30am",
        disabled: checkTimeSlot(moment().hour(11).minute(30)),
      },
      {
        _id: 6,
        group: 1,
        value: 6,
        option: "12:00pm",
        disabled: checkTimeSlot(moment().hour(12).minute(0)),
      },
      {
        _id: 7,
        group: 1,
        value: 7,
        option: "12:30pm",
        disabled: checkTimeSlot(moment().hour(12).minute(30)),
      },
      {
        _id: 8,
        group: 1,
        value: 8,
        option: "1:00pm",
        disabled: checkTimeSlot(moment().hour(13).minute(0)),
      },
      {
        _id: 9,
        group: 1,
        value: 9,
        option: "1:30pm",
        disabled: checkTimeSlot(moment().hour(13).minute(30)),
      },
      {
        _id: 10,
        group: 1,
        value: 10,
        option: "2:00pm",
        disabled: checkTimeSlot(moment().hour(14).minute(0)),
      },
      {
        _id: 11,
        group: 1,
        value: 11,
        option: "2:30pm",
        disabled: checkTimeSlot(moment().hour(14).minute(30)),
      },
      {
        _id: 12,
        group: 1,
        value: 12,
        option: "3:00pm",
        disabled: checkTimeSlot(moment().hour(15).minute(0)),
      },
      {
        _id: 13,
        group: 1,
        value: 13,
        option: "3:30pm",
        disabled: checkTimeSlot(moment().hour(15).minute(30)),
      },
      {
        _id: 14,
        group: 1,
        value: 14,
        option: "4:00pm",
        disabled: checkTimeSlot(moment().hour(16).minute(0)),
      },
      {
        _id: 15,
        group: 1,
        value: 15,
        option: "4:30pm",
        disabled: checkTimeSlot(moment().hour(16).minute(30)),
      },
      {
        _id: 16,
        group: 1,
        value: 16,
        option: "5:00pm",
        disabled: checkTimeSlot(moment().hour(17).minute(0)),
      },
      {
        _id: 17,
        group: 1,
        value: 17,
        option: "5:30pm",
        disabled: checkTimeSlot(moment().hour(17).minute(30)),
      },
      {
        _id: 18,
        group: 2,
        value: 18,
        option: "6:00pm",
        disabled: checkTimeSlot(moment().hour(18).minute(0)),
      },
      {
        _id: 19,
        group: 2,
        value: 19,
        option: "6:30pm",
        disabled: checkTimeSlot(moment().hour(18).minute(30)),
      },
      {
        _id: 20,
        group: 2,
        value: 20,
        option: "7:00pm",
        disabled: checkTimeSlot(moment().hour(19).minute(0)),
      },
    ];

    // ADDED [10/07/2026] - protection-review ends at 5:30pm (Morning/Afternoon
    // groups only), so slots after 5:30pm are dropped for this route.
    // Previously this function returned `allSlots` directly with `[checkTimeSlot]` as its only dep.
    if (isProtectionReview) {
      return allSlots.filter((slot) => slot.value <= 17);
    }

    return allSlots;
  }, [checkTimeSlot, isProtectionReview]);

  useEffect(() => {
    const dateGroupSize = 5;
    const allDates = buildDates();
    const slicedDates = slice(allDates, dateOptionsPage * dateGroupSize);
    const trimmedDateGroup = take(slicedDates, dateGroupSize);
    setDateOptions(trimmedDateGroup);
  }, [dateOptionsPage, buildDates]);

  // FIX: 'data' removed from deps — setData uses functional update (prev => ...)
  // so data is never needed in the closure. Previously caused an infinite loop.
  useEffect(() => {
    const { name } = formDataChange;

    if (name === "appointmentDay")
      setData((prev) => ({
        ...prev,
        appointmentGroup: null,
        appointmentTime: null,
      }));

    if (name === "appointmentCallSoon")
      setData((prev) => ({
        ...prev,
        appointmentDay: null,
        appointmentGroup: null,
        appointmentTime: null,
      }));

    if (
      name === "appointmentDay" ||
      name === "appointmentGroup" ||
      name === "appointmentTime"
    )
      setData((prev) => {
        if (prev.appointmentCallSoon === 1) {
          return { ...prev, appointmentCallSoon: 0 };
        }
        return prev; // no change needed — return same ref to avoid re-render
      });
  }, [formDataChange, setData]);

  useEffect(() => {
    const { appointmentDay, appointmentTime } = data;

    const getDateText = (val) => moment(val).format("Do MMMM, YYYY");

    const getDayText = (val) =>
      isToday(val)
        ? "Today"
        : isTomorrow(val)
        ? "Tomorrow"
        : moment(val).format("dddd");

    const dayDifference = moment(appointmentDay).diff(moment(), "days");

    const currentTimeSlots = buildTimeSlots();
    const filteredTime = currentTimeSlots.filter(
      (timeSlot) => timeSlot.value === appointmentTime,
    );

    const time = appointmentTime ? filteredTime[0].option : "";

    const text =
      appointmentDay !== null && appointmentTime !== null
        ? dayDifference >= 6
          ? `${getDateText(appointmentDay)}. ${time}`
          : `${getDayText(appointmentDay)}, ${time} (${getDateText(
              appointmentDay,
            )})`
        : "Call as soon as possible";

    setAppointment({
      date:
        appointmentDay && appointmentTime
          ? moment(appointmentDay).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
      day:
        appointmentDay && appointmentTime
          ? getDayText(appointmentDay)
          : "Today",
      time,
      text,
    });
  }, [data, setAppointment, buildTimeSlots, isToday, isTomorrow]);

  const handleChange = ({ currentTarget }) => {
    const input = { name: currentTarget.name, value: currentTarget.value };
    doAfterChange(input);
  };

  const handleRadioChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: parseInt(currentTarget.value),
    };
    doAfterChange(input);
  };

  const handleCheckboxChange = ({ currentTarget }) => {
    const input = {
      name: currentTarget.name,
      value: parseInt(currentTarget.value) === 0 ? 1 : 0,
    };
    doAfterChange(input);
  };

  const doAfterChange = (input) => {
    setData({ ...data, [input.name]: input.value });
    setFormDataChange(input);
  };

  const handleDateGroupSelect = (value) => {
    const page = value === "prev" ? dateOptionsPage - 1 : dateOptionsPage + 1;
    setDateOptionsPage(page);
  };

  // Stable copy for JSX rendering
  const timeSlots = buildTimeSlots();

  const getTimeSlots = (appointmentGroup) =>
    timeSlots.filter((timeSlot) => appointmentGroup === timeSlot.group);

  const checkTimeOfDay = (time) => {
    return isToday(data.appointmentDay)
      ? moment.duration(moment().diff(moment(time))).asHours() > -0.5
      : false;
  };

  return (
    <>
      <div className="mb-2 flex justify-center">
        <div className="w-full max-w-sm sm:max-w-full">
          <RadioGroup
            name="appointmentDay"
            label="Day"
            labelCheckMark={false}
            options={dateOptions}
            layout="date"
            toolTip={null}
            value={data.appointmentDay}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="mb-4 flex justify-center sm:justify-start">
        <div className="w-full max-w-sm sm:max-w-full">
          <div className="flex h-10 w-full gap-2">
            <ButtonSmall
              color="white"
              label={<HiChevronLeft size={32} />}
              onClick={() => handleDateGroupSelect("prev")}
              disabled={dateOptionsPage === 0}
            />
            <ButtonSmall
              color="white"
              label={<HiChevronRight size={32} />}
              onClick={() => handleDateGroupSelect("next")}
              disabled={dateOptionsPage === 3}
            />
          </div>
        </div>
      </div>

      {data.appointmentDay !== null ? (
        <>
          <div className="mb-4 flex justify-center">
            <div className="w-full max-w-sm sm:max-w-full">
              <RadioGroup
                name="appointmentGroup"
                label="Time of day"
                labelCheckMark={false}
                options={
                  // AMENDED [10/07/2026] - protection-review has no evening slots
                  // (6pm now sits in the afternoon group), so the Evening button
                  // is hidden entirely for this route.
                  // Previously this options array was passed inline with no filtering:
                  // [
                  //   { _id: 0, value: 0, option: "Morning", disabled: checkTimeOfDay(moment().hour(12).minute(0)) },
                  //   { _id: 1, value: 1, option: "Afternoon", disabled: checkTimeOfDay(moment().hour(17).minute(0)) },
                  //   { _id: 2, value: 2, option: "Evening", disabled: isWeekend(data.appointmentDay) ? true : checkTimeOfDay(moment().hour(19).minute(0)) },
                  // ]
                  [
                    {
                      _id: 0,
                      value: 0,
                      option: "Morning",
                      disabled: checkTimeOfDay(moment().hour(12).minute(0)),
                    },
                    {
                      _id: 1,
                      value: 1,
                      option: "Afternoon",
                      disabled: checkTimeOfDay(moment().hour(17).minute(0)),
                    },
                    {
                      _id: 2,
                      value: 2,
                      option: "Evening",
                      disabled: isWeekend(data.appointmentDay)
                        ? true
                        : checkTimeOfDay(moment().hour(19).minute(0)),
                    },
                  ].filter((option) =>
                    isProtectionReview ? option.value !== 2 : true,
                  )
                }
                toolTip={null}
                value={data.appointmentGroup}
                onChange={handleRadioChange}
              />
            </div>
          </div>

          {data.appointmentGroup !== null ? (
            <div className="mb-4 flex justify-center">
              <div className="w-full max-w-sm sm:max-w-full">
                <RadioGroup
                  name="appointmentTime"
                  label="Time slot"
                  labelCheckMark={false}
                  options={getTimeSlots(data.appointmentGroup)}
                  layout="time"
                  toolTip={null}
                  value={data.appointmentTime}
                  onChange={handleRadioChange}
                />
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="mb-0 flex justify-center sm:justify-start">
        <div className="w-full max-w-sm sm:max-w-full">
          <SingleCheckbox
            name="appointmentCallSoon"
            label="Call as soon as possible"
            size="large"
            itemsAlign="center"
            help={null}
            value={data.appointmentCallSoon}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
    </>
  );
};

export default AppointmentPicker;
